<script setup>
import {
  computed,
  nextTick,
  ref,
  watch,
} from 'vue';

import TicketsAPI from 'dashboard/api/tickets';
import { useAlert } from 'dashboard/composables';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'refresh',
]);

const composerMode = ref('reply');
const content = ref('');
const attachments = ref([]);
const fileInput = ref(null);
const timeline = ref(null);
const isSending = ref(false);

const messages = computed(
  () => props.ticket?.messages || []
);

const canSend = computed(() => {
  return (
    content.value.trim().length > 0 ||
    attachments.value.length > 0
  );
});

const formatDate = value => {
  if (!value) return '';

  const date = new Date(Number(value) * 1000);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const authorName = message => {
  if (message?.author?.name) {
    return message.author.name;
  }

  if (message.direction === 'system') {
    return 'Sistema';
  }

  return 'JOV Tickets';
};

const bubbleClasses = message => {
  if (
    message.visibility === 'private' ||
    message.direction === 'internal'
  ) {
    return [
      'border-amber-200',
      'bg-amber-50',
      'dark:border-amber-800/50',
      'dark:bg-amber-950/20',
    ];
  }

  if (message.direction === 'outbound') {
    return [
      'border-n-brand/20',
      'bg-n-brand/5',
    ];
  }

  if (message.direction === 'system') {
    return [
      'border-n-weak',
      'bg-n-alpha-1',
    ];
  }

  return [
    'border-n-weak',
    'bg-n-solid-1',
  ];
};

const messageIcon = message => {
  if (
    message.visibility === 'private' ||
    message.direction === 'internal'
  ) {
    return 'i-lucide-lock-keyhole';
  }

  if (message.direction === 'inbound') {
    return 'i-lucide-message-circle-reply';
  }

  if (message.direction === 'outbound') {
    return 'i-lucide-send';
  }

  return 'i-lucide-info';
};

// JOV_V4_TIMELINE

const isPrivateMessage = message => {
  return (
    message?.visibility === 'private' ||
    message?.direction === 'internal'
  );
};

const messageInitials = message => {
  const name = authorName(message);

  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const messageRowClass = message => {
  if (message?.direction === 'system') {
    return 'is-system';
  }

  if (isPrivateMessage(message)) {
    return 'is-private';
  }

  if (message?.direction === 'outbound') {
    return 'is-outbound';
  }

  return 'is-inbound';
};

const messageCountLabel = computed(() => {
  const count = messages.value.length;

  return `${count} mensage${count === 1 ? 'm' : 'ns'}`;
});


const scrollToBottom = async () => {
  await nextTick();

  if (!timeline.value) return;

  timeline.value.scrollTop =
    timeline.value.scrollHeight;
};

const selectFiles = event => {
  const selected =
    Array.from(event.target.files || []);

  const room =
    Math.max(
      0,
      15 - attachments.value.length
    );

  attachments.value.push(
    ...selected.slice(0, room)
  );

  event.target.value = '';
};

const removeFile = index => {
  attachments.value.splice(index, 1);
};

const buildPayload = () => {
  const key =
    composerMode.value === 'note' ?
      'note' :
      'message';

  const form = new FormData();

  form.append(
    `${key}[content]`,
    content.value.trim()
  );

  form.append(
    `${key}[content_type]`,
    'text'
  );

  attachments.value.forEach(file => {
    form.append(
      `${key}[attachments][]`,
      file
    );
  });

  return form;
};

const send = async () => {
  if (!canSend.value || isSending.value) {
    return;
  }

  isSending.value = true;

  try {
    const payload = buildPayload();

    if (composerMode.value === 'note') {
      await TicketsAPI.createNote(
        props.ticket.id,
        payload
      );
    } else {
      await TicketsAPI.createMessage(
        props.ticket.id,
        payload
      );
    }

    content.value = '';
    attachments.value = [];

    emit('refresh');

    await scrollToBottom();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível registrar a mensagem.'
    );
  } finally {
    isSending.value = false;
  }
};

watch(
  () => props.ticket?.id,
  () => {
    content.value = '';
    attachments.value = [];
    scrollToBottom();
  }
);

watch(
  () => messages.value.length,
  () => scrollToBottom()
);
</script>

<template>
  <div class="jov-timeline">
    <header class="jov-timeline-header">
      <div class="jov-timeline-title">
        <div class="jov-timeline-meta">
          <span class="jov-ticket-number">
            {{ ticket.number }}
          </span>

          <span
            v-if="ticket.status?.name"
            class="jov-ticket-status-pill"
          >
            <span class="jov-status-dot" />

            {{ ticket.status.name }}
          </span>
        </div>

        <h2>
          {{ ticket.subject }}
        </h2>

        <p>
          Atendimento registrado no JOV Tickets
        </p>
      </div>

      <div class="jov-timeline-origin">
        <span class="i-lucide-waypoints" />

        <div>
          <strong>
            {{
              ticket.origin?.label ||
              ticket.origin?.display ||
              ticket.source ||
              'Ticket'
            }}
          </strong>

          <span>
            {{ formatDate(ticket.updated_at) }}
          </span>
        </div>
      </div>
    </header>

    <nav class="jov-timeline-tabs">
      <div class="jov-timeline-tab is-active">
        <span class="i-lucide-messages-square" />
        Conversação
      </div>

      <span class="jov-timeline-count">
        {{ messageCountLabel }}
      </span>
    </nav>

    <div
      ref="timeline"
      class="jov-timeline-scroll"
    >
      <div
        v-if="loading"
        class="jov-empty-state"
      >
        <Spinner class="text-n-brand" />
      </div>

      <div
        v-else-if="!messages.length"
        class="jov-empty-state"
      >
        <span class="i-lucide-messages-square" />

        <strong>
          Ticket sem histórico
        </strong>

        <p>
          A primeira mensagem aparecerá aqui.
        </p>
      </div>

      <div
        v-else
        class="jov-message-list"
      >
        <article
          v-for="message in messages"
          :key="message.id"
          class="jov-message-row"
          :class="messageRowClass(message)"
        >
          <template
            v-if="
              message.direction === 'system'
            "
          >
            <div class="jov-system-message">
              <span class="i-lucide-info" />

              <span>
                {{
                  message.content ||
                  'Atualização do ticket'
                }}
              </span>

              <time>
                {{
                  formatDate(
                    message.created_at
                  )
                }}
              </time>
            </div>
          </template>

          <template v-else>
            <div class="jov-message-avatar">
              {{ messageInitials(message) }}
            </div>

            <div class="jov-message-content">
              <div class="jov-message-author">
                <strong>
                  {{ authorName(message) }}
                </strong>

                <span
                  v-if="
                    isPrivateMessage(message)
                  "
                  class="jov-private-pill"
                >
                  <span
                    class="i-lucide-lock-keyhole"
                  />

                  Nota privada
                </span>

                <time>
                  {{
                    formatDate(
                      message.created_at
                    )
                  }}
                </time>
              </div>

              <div class="jov-message-bubble">
                <p
                  v-if="message.content"
                >
                  {{ message.content }}
                </p>

                <div
                  v-if="
                    message.attachments?.length
                  "
                  class="jov-message-attachments"
                >
                  <template
                    v-for="
                      attachment in
                      message.attachments
                    "
                    :key="attachment.id"
                  >
                    <a
                      v-if="
                        attachment.file_type ===
                          'image' &&
                        attachment.data_url
                      "
                      :href="
                        attachment.data_url
                      "
                      target="_blank"
                      rel="noopener noreferrer"
                      class="jov-image-attachment"
                    >
                      <img
                        :src="
                          attachment.data_url
                        "
                        :alt="
                          attachment.file_name ||
                          'Imagem'
                        "
                      />
                    </a>

                    <a
                      v-else-if="
                        attachment.data_url
                      "
                      :href="
                        attachment.data_url
                      "
                      target="_blank"
                      rel="noopener noreferrer"
                      class="jov-file-attachment"
                    >
                      <span
                        class="i-lucide-paperclip"
                      />

                      <span>
                        {{
                          attachment.file_name ||
                          attachment.fallback_title ||
                          'Anexo'
                        }}
                      </span>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </article>
      </div>
    </div>

    <footer class="jov-composer">
      <div class="jov-composer-tabs">
        <button
          type="button"
          :class="{
            'is-active':
              composerMode === 'reply',
          }"
          @click="
            composerMode = 'reply'
          "
        >
          <span class="i-lucide-send" />
          Responder
        </button>

        <button
          type="button"
          :class="{
            'is-active':
              composerMode === 'note',
          }"
          @click="
            composerMode = 'note'
          "
        >
          <span
            class="i-lucide-lock-keyhole"
          />
          Nota privada
        </button>
      </div>

      <div
        class="jov-composer-box"
        :class="{
          'is-note':
            composerMode === 'note',
        }"
      >
        <textarea
          v-model="content"
          rows="3"
          :placeholder="
            composerMode === 'note'
              ? 'Escreva uma nota interna...'
              : 'Digite sua mensagem...'
          "
          @keydown.ctrl.enter.prevent="send"
          @keydown.meta.enter.prevent="send"
        />

        <div
          v-if="attachments.length"
          class="jov-composer-attachments"
        >
          <span
            v-for="
              (file, index) in attachments
            "
            :key="
              `${file.name}-${index}`
            "
          >
            <span
              class="i-lucide-paperclip"
            />

            <span>
              {{ file.name }}
            </span>

            <button
              type="button"
              class="i-lucide-x"
              @click="
                removeFile(index)
              "
            />
          </span>
        </div>

        <div class="jov-composer-toolbar">
          <div class="jov-composer-tools">
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              @change="selectFiles"
            />

            <button
              type="button"
              class="jov-composer-tool"
              title="Adicionar anexo"
              @click="
                fileInput?.click()
              "
            >
              <span
                class="i-lucide-paperclip"
              />
            </button>

            <span class="jov-composer-shortcut">
              Ctrl + Enter para enviar
            </span>
          </div>

          <button
            type="button"
            class="jov-send-button"
            :disabled="
              !canSend || isSending
            "
            @click="send"
          >
            <Spinner
              v-if="isSending"
              class="text-white"
            />

            <span
              v-else
              :class="
                composerMode === 'note'
                  ? 'i-lucide-lock-keyhole'
                  : 'i-lucide-send'
              "
            />

            {{
              composerMode === 'note'
                ? 'Adicionar nota'
                : 'Enviar'
            }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>
